/**
 * desktopWarmCache
 *
 * Tracks which Desktops (Spaces) are allowed to keep their per-user CRDT
 * connection (Y.Doc + IndexedDB + WebSocket provider) alive in the background
 * instead of being torn down every time the user switches away. This is what
 * makes revisiting a recently used Desktop instant — its Y.Doc kept receiving
 * live remote updates the whole time (Yjs merges those into the doc
 * regardless of whether the store observer is mirroring them — see the
 * SpaceMismatch guard in spaceCRDTService.setupSpaceObserver), so switching
 * back only needs a synchronous snapshot read instead of a full
 * fetch + IndexedDB replay + WebSocket handshake.
 *
 * This module is pure LRU bookkeeping with no side effects and no knowledge
 * of CRDT/Yjs — it just answers "who should I evict now that I'm promoting
 * this one?". spaceStore.initialize() is the single call site that consults
 * `promote()` and performs the actual `spaceCRDTService.disconnectSpace()`
 * for whatever falls out of the warm set. Keeping the LRU logic here (instead
 * of duplicated across spaceStore/spaceCRDTService) means there is exactly
 * one place that decides how many Desktops stay warm.
 */

export const MAX_WARM_DESKTOPS = 10;

class DesktopWarmCache {
  /** Most-recently-visited spaceId first. */
  private order: string[] = [];

  /**
   * Mark `spaceId` as the most recently visited Desktop. If this pushes the
   * warm set beyond MAX_WARM_DESKTOPS, returns the spaceId that fell off the
   * tail — the caller must disconnect its CRDT state. Returns null when
   * nothing needs to be evicted.
   */
  promote(spaceId: string): string | null {
    this.order = [spaceId, ...this.order.filter((id) => id !== spaceId)];
    if (this.order.length > MAX_WARM_DESKTOPS) {
      return this.order.pop() ?? null;
    }
    return null;
  }

  /** True if `spaceId` is currently within the warm set. */
  has(spaceId: string): boolean {
    return this.order.includes(spaceId);
  }

  /**
   * Explicitly drop a space (e.g. it was deleted, or the user logged out)
   * without evicting anything else in its place.
   */
  remove(spaceId: string): void {
    this.order = this.order.filter((id) => id !== spaceId);
  }

  /** Current warm order, most-recent first. For debugging/tests only. */
  snapshot(): string[] {
    return [...this.order];
  }

  reset(): void {
    this.order = [];
  }
}

export const desktopWarmCache = new DesktopWarmCache();

if (typeof window !== 'undefined') {
  (window as any).__desktopWarmCache = desktopWarmCache;
}
